import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useMemo } from "react";
import { DoubleArrowDownIcon } from "@radix-ui/react-icons";

import { getAllLeaveApplicationQueryOptions } from "@/config/queries/query-options";
import { DataTable } from "./_components/leave-application-table/data-table";
import { columns } from "./_components/leave-application-table/columns";
import { useStatusFilter } from "./hooks/use-status-filter";

import { Label } from "@/components/ui/label/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group/radio-group";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { Button } from "@/components/ui/button/button";
import { Spinner } from "@/components/ui/spinner/spinner";
import { Separator } from "@/components/ui/separator/separator";
import { ViewLeaveApplicationModal } from "./_components/view-leave-application-modal/view-leave-application-modal";
import { DeleteLeaveApplicationModal } from "./_components/delete-leave-application/delete-leave-modal";

const AdminLeaveApplicationsPage = () => {
  const adminKey = Cookies.get("key") as string;
  const { filter, setFilter } = useStatusFilter();

  const { data, isPending, isFetching } = useQuery(
    getAllLeaveApplicationQueryOptions(adminKey, filter)
  );

  const leaveApplicationData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.LeaveApplication === undefined) {
      return [];
    }

    const result = data?.LeaveApplication?.map((la) => {
      return {
        id: la?._id,
        leaveType: la?.leaveType,
        reason: la?.reason,
        startDate: la?.startDate,
        endDate: la?.endDate,
        status: la?.status,
        employeeName: la?.employee?.name,
        employeeEmail: la?.employee?.email,
        createdAt: la?.createdAt,
        attachment: la?.attachment,
        isPaid: la?.isPaid,
        isHalfDay: la?.isHalfDay,
      };
    });

    return result?.sort(
      (a, b) =>
        new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
    );
  }, [isPending, isFetching, data]);

  return (
    <div className="p-5 h-[calc(100%-56px)]">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-medium">Leave Applications</h3>

        <Popover>
          <PopoverTrigger>
            <Button
              className="mr-3 flex items-center gap-2"
              size="sm"
              variant="outline"
            >
              <DoubleArrowDownIcon className="w-4 h-4" />
              Filter
              {filter && (
                <>
                  <Separator orientation="vertical" />
                  {filter} ({leaveApplicationData?.length})
                </>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <RadioGroup
              value={filter}
              onValueChange={(
                value: "Pending" | "Approved" | "Rejected" | "Cancelled"
              ) => {
                setFilter(value);
              }}
            >
              <div className="flex items-center space-x-2 cursor-pointer w-fit">
                <RadioGroupItem value="Pending" id="pending" />
                <Label htmlFor="pending">Pending</Label>
              </div>
              <div className="flex items-center space-x-2 cursor-pointer w-fit">
                <RadioGroupItem value="Approved" id="approved" />
                <Label htmlFor="approved">Approved</Label>
              </div>
              <div className="flex items-center space-x-2 cursor-pointer w-fit">
                <RadioGroupItem value="Rejected" id="rejected" />
                <Label htmlFor="rejected">Rejected</Label>
              </div>
              <div className="flex items-center space-x-2 cursor-pointer w-fit">
                <RadioGroupItem value="Cancelled" id="cancelled" />
                <Label htmlFor="cancelled">Cancelled</Label>
              </div>
            </RadioGroup>
          </PopoverContent>
        </Popover>
      </div>

      {isPending ? (
        <div className="h-20 mt-10 flex items-center justify-center">
          <Spinner size="small">
            <p>Searching for Leave Applications...</p>
          </Spinner>
        </div>
      ) : data?.LeaveApplication?.length ? (
        <div className="mt-5">
          <DataTable data={leaveApplicationData} columns={columns} />
        </div>
      ) : (
        <div className="border h-20 flex items-center justify-center mt-5">
          No {filter} Leave Applications
        </div>
      )}

      <ViewLeaveApplicationModal />
      <DeleteLeaveApplicationModal />
    </div>
  );
};

export default AdminLeaveApplicationsPage;
